/**
 * Smoke Drive file names must keep a real type (e.g. .jpg), even when the
 * stored name is a long URL/base64 stem with the extension sliced off.
 */

export const DRIVE_FILE_NAME_MAX = 180;

const CONTENT_TYPE_EXTENSION = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/pjpeg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/bmp': '.bmp',
    'image/svg+xml': '.svg',
    'image/tiff': '.tif',
    'image/heic': '.heic',
    'image/heif': '.heif',
    'image/avif': '.avif',
    'application/pdf': '.pdf',
    'text/plain': '.txt',
    'text/csv': '.csv',
    'text/html': '.html',
    'text/css': '.css',
    'text/markdown': '.md',
    'application/json': '.json',
    'application/xml': '.xml',
    'text/xml': '.xml',
    'application/zip': '.zip',
    'application/x-zip-compressed': '.zip',
    'application/gzip': '.gz',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'application/rtf': '.rtf',
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
    'video/mp4': '.mp4',
    'video/quicktime': '.mov',
};

/**
 * @param {string} [contentType]
 * @returns {string} including the leading dot, or ''
 */
export function extensionFromContentType(contentType) {
    const type = String(contentType || '').split(';')[0].trim().toLowerCase();
    if (!type || type === 'application/octet-stream') return '';
    if (CONTENT_TYPE_EXTENSION[type]) return CONTENT_TYPE_EXTENSION[type];
    const image = type.match(/^image\/([a-z0-9.-]+)$/);
    if (image) {
        const sub = image[1] === 'jpeg' ? 'jpg' : image[1];
        return `.${sub}`;
    }
    return '';
}

/**
 * @param {string} [name]
 * @returns {string} including the leading dot, or ''
 */
export function extensionFromFileName(name) {
    const base = String(name || '').split(/[/\\]/).pop() || '';
    const match = base.match(/(\.[a-z0-9]{1,8})$/i);
    return match ? match[1].toLowerCase() : '';
}

/**
 * Truncate a name to 180 chars without chopping off the type suffix.
 * If the name has no suffix, use contentType (image/jpeg -> .jpg).
 *
 * @param {string} [name]
 * @param {string} [contentType]
 * @returns {string}
 */
export function ensureDriveFileExtension(name, contentType) {
    const cleaned = String(name || 'file').trim() || 'file';
    const fromName = extensionFromFileName(cleaned);
    const ext = fromName || extensionFromContentType(contentType) || '';
    const stemRaw = fromName ? cleaned.slice(0, -fromName.length) : cleaned;
    const stem = (stemRaw.replace(/[. ]+$/g, '') || 'file');
    const full = ext ? `${stem}${ext}` : stem;
    if (full.length <= DRIVE_FILE_NAME_MAX) return full;
    const budget = Math.max(8, DRIVE_FILE_NAME_MAX - ext.length);
    return `${stem.slice(0, budget)}${ext}`;
}

/**
 * @param {{ originalFileName?: string, fileName?: string, name?: string, contentType?: string }} [item]
 * @returns {string}
 */
export function driveDownloadFileName(item) {
    const raw = item?.name || item?.fileName || item?.originalFileName || 'file';
    if (extensionFromFileName(raw)) {
        return ensureDriveFileExtension(raw, item?.contentType);
    }
    const fromOriginal = extensionFromFileName(item?.originalFileName);
    if (fromOriginal) {
        return ensureDriveFileExtension(`${raw}${fromOriginal}`, item?.contentType);
    }
    return ensureDriveFileExtension(raw, item?.contentType);
}

/**
 * Storage Content-Disposition so the browser saves with the typed name.
 * @param {string} fileName
 * @returns {string}
 */
export function contentDispositionForDriveFile(fileName) {
    const ascii = String(fileName || 'file')
        .replace(/[^\x20-\x7E]/g, '_')
        .replace(/["\\]/g, '_');
    const encoded = encodeURIComponent(String(fileName || 'file'));
    return `attachment; filename="${ascii}"; filename*=UTF-8''${encoded}`;
}
